import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { FilterState } from '../types/filters';

// Firestore collection for saved filters
const SAVED_FILTERS_COLLECTION = 'savedFilters';

export interface SavedFilter {
  id: string;
  userId: string;  // User's email
  name: string;    // User-provided name
  filter: FilterState;
  createdAt: Date;
  updatedAt: Date;
}

class FilterService {
  // Save a new filter
  async saveFilter(name: string, filter: FilterState): Promise<string> {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('User not authenticated');
    
    const filterData = {
      userId: user.email,
      name,
      filter,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, SAVED_FILTERS_COLLECTION), filterData);
    return docRef.id;
  }
  
  // Update an existing filter
  async updateFilter(filterId: string, name: string, filter: FilterState): Promise<void> {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('User not authenticated');
    
    const filterRef = doc(db, SAVED_FILTERS_COLLECTION, filterId);
    await updateDoc(filterRef, {
      name,
      filter,
      updatedAt: new Date()
    });
  }
  
  // Get user's saved filters
  async getUserFilters(): Promise<SavedFilter[]> {
    const user = auth.currentUser;
    if (!user || !user.email) return [];
    
    try {
      // First try with the compound query (requires index)
      const filtersQuery = query(
        collection(db, SAVED_FILTERS_COLLECTION),
        where('userId', '==', user.email),
        orderBy('updatedAt', 'desc')
      );
      
      const snapshot = await getDocs(filtersQuery);
      return snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      } as SavedFilter));
    } catch (error) {
      console.error('Error with compound query, falling back to simple query:', error);
      
      // Fallback to a simpler query without orderBy if index doesn't exist
      try {
        const simpleQuery = query(
          collection(db, SAVED_FILTERS_COLLECTION),
          where('userId', '==', user.email)
        );
        
        const snapshot = await getDocs(simpleQuery);
        // Sort in memory instead
        const filters = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        } as SavedFilter));
        
        // Sort by updatedAt in descending order
        return filters.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      } catch (fallbackError) {
        console.error('Error with fallback query:', fallbackError);
        return [];
      }
    }
  }
  
  // Check if a filter name already exists for the user
  async filterNameExists(name: string): Promise<boolean> {
    const user = auth.currentUser;
    if (!user || !user.email) return false;
    
    const filtersQuery = query(
      collection(db, SAVED_FILTERS_COLLECTION),
      where('userId', '==', user.email),
      where('name', '==', name)
    );
    
    const snapshot = await getDocs(filtersQuery);
    return !snapshot.empty;
  }
  
  // Delete a filter
  async deleteFilter(filterId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('User not authenticated');
    
    await deleteDoc(doc(db, SAVED_FILTERS_COLLECTION, filterId));
  }
}

export const filterService = new FilterService();
